import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AppNode, Connection } from '../types';
import type { VideoSource } from '../config/featuredTemplates';
import { API_BASE } from '../config';

/**
 * v1.4.108 作品发布 store（上传作品功能）
 * - publishedWorks：用户发布到 Bong作品页的作品（图像/视频），独立页 /bongworks 展示
 *   （首页精选板块为纯模板内容，不再混入用户作品）
 * - libraryAssets：历史图库资产（与编辑器 assetHistory 条目同构），AppNew 监听 assetHistory
 *   变化时同步进来；发布成功后本次上传的文件也会归档，保证「历史图库」跨视图可用
 * - persist localStorage：编辑器发布 / Bong作品页展示两处共享同一份数据；
 *   workflowData 为「公开制作流程」开启时发布瞬间的源项目画布快照（全部节点与连线），
 *   「查看制作流程」只读画布与「复制项目」都以它为数据源（与编辑器真实节点一致）
 * - 演示数据：store 预置 DEMO_WORKS（id 带 demo- 前缀），让 /bongworks 首次进入即有
 *   内容可看；已持久化过空列表的旧数据经 version 2 迁移补种，已有真实作品则原样保留
 */

/** 公开制作流程快照（发布瞬间源项目画布的全部节点/连线/分组，可直接作为 workflow_data 落库） */
export interface WorkflowSnapshot {
  nodes: AppNode[];
  connections: Connection[];
  groups: any[];
}

export interface PublishedWork {
  id: string;
  type: 'image' | 'video';
  /** 作品主文件地址（图片作品 = 封面首图；视频作品 = 视频地址） */
  url: string;
  /** 视频封面（空则展示端用 <video preload=metadata> 显示首帧） */
  coverUrl?: string;
  /** 视频多档清晰度源（后端异步转码，发布响应时通常尚未生成 → 播放器按 url 懒查询兜底） */
  sources?: VideoSource[];
  /** 图片作品多图（含首图） */
  images?: string[];
  /** 封面自然尺寸（图像作品 = 主图）：Bong作品页瀑布流按宽高比换算卡高；
      旧记录/演示数据可缺省，展示端运行时测量兜底 */
  coverWidth?: number;
  coverHeight?: number;
  title: string;
  categories: string[];
  prompt: string;
  /** 是否公开制作流程（开启时发布快照 workflowData，「查看制作流程」入口依此显示） */
  isPublicWorkflow: boolean;
  /** 公开制作流程快照：发布瞬间源项目的全部节点/连线/分组（旧记录/演示数据无此字段） */
  workflowData?: WorkflowSnapshot;
  likes: number;
  createdAt: number;
  authorName: string;
  authorAvatar?: string;
}

/** 发布作品请求体（UploadWorkModal → 后端 POST /api/works；作者字段由后端按登录用户生成） */
export interface WorkPublishPayload {
  type: 'image' | 'video';
  /** 作品主文件地址（图片作品 = 封面首图；视频作品 = 视频地址） */
  url: string;
  coverUrl?: string;
  sources?: VideoSource[];
  images?: string[];
  coverWidth?: number;
  coverHeight?: number;
  title: string;
  categories: string[];
  prompt: string;
  isPublicWorkflow: boolean;
  workflowData?: WorkflowSnapshot;
}

export interface LibraryAsset {
  id: string;
  type: 'image' | 'video' | 'audio';
  src: string;
  title: string;
  timestamp: number;
  /** 生成该资产的提示词（上传作品选历史图库时回填提示词栏） */
  prompt?: string;
}

/* —— 演示作品（Bong作品页效果预览）：素材复用站内瀑布流图与视频封面/视频文件，
   id 统一 demo- 前缀便于与真实发布区分；createdAt 依次递减保持「最新在前」 —— */
const HOUR = 3600 * 1000;
const NOW = Date.now();

const DEMO_WORKS: PublishedWork[] = [
  {
    id: 'demo-img-01',
    type: 'image',
    url: '/images/home/waterfall/wf-01.png',
    title: '赛博朋克城市夜景 · 霓虹雨巷',
    categories: ['产品宣传KV'],
    prompt: '赛博朋克风格城市街道夜景，霓虹灯牌倒映在湿漉漉的路面，电影感构图',
    isPublicWorkflow: true,
    likes: 328,
    createdAt: NOW - 2 * HOUR,
    authorName: '创作者小林',
  },
  {
    id: 'demo-img-02',
    type: 'image',
    url: '/images/home/waterfall/wf-02.png',
    title: '国风水墨山水 · 云雾留白',
    categories: ['产品宣传KV'],
    prompt: '中国水墨画风格山水，大面积留白，云雾缭绕，远山淡影',
    isPublicWorkflow: false,
    likes: 1024,
    createdAt: NOW - 5 * HOUR,
    authorName: '阿澈',
  },
  {
    id: 'demo-img-03',
    type: 'image',
    url: '/images/home/waterfall/wf-03.png',
    title: '3C 产品渲染 · 极简白底',
    categories: ['电商详情'],
    prompt: '极简白底产品渲染图，柔和顶光，浅灰投影，电商主图风格',
    isPublicWorkflow: true,
    likes: 512,
    createdAt: NOW - 9 * HOUR,
    authorName: '设计师Momo',
  },
  {
    id: 'demo-img-04',
    type: 'image',
    url: '/images/home/waterfall/wf-04.png',
    title: '复古胶片人像 · 暖调光影',
    categories: ['产品宣传KV'],
    prompt: '复古胶片质感人像，暖黄色调，窗边自然光，颗粒感',
    isPublicWorkflow: false,
    likes: 86,
    createdAt: NOW - 26 * HOUR,
    authorName: '老张爱摄影',
  },
  {
    id: 'demo-img-05',
    type: 'image',
    url: '/images/home/waterfall/wf-05.png',
    title: '奇幻森林 · 体积光场景',
    categories: ['产品宣传KV'],
    prompt: '魔幻森林场景，丁达尔体积光穿透树冠，苔藓地面，薄雾弥漫',
    isPublicWorkflow: true,
    likes: 2157,
    createdAt: NOW - 33 * HOUR,
    authorName: '星野',
  },
  {
    id: 'demo-img-06',
    type: 'image',
    url: '/images/home/waterfall/wf-06.png',
    title: '美食静物 · 晨光餐桌',
    categories: ['电商详情'],
    prompt: '清晨柔和光线下的美食静物，木质餐桌，浅景深，生活方式摄影',
    isPublicWorkflow: false,
    likes: 143,
    createdAt: NOW - 50 * HOUR,
    authorName: '甜妹厨房',
  },
  {
    id: 'demo-vid-01',
    type: 'video',
    url: '/videos/v-01.mp4',
    coverUrl: '/images/home/videos/v-01.png',
    title: '城市延时 · 车流光轨',
    categories: ['产品宣传KV'],
    prompt: '城市夜景延时摄影，车流拉出光轨，高楼灯火',
    isPublicWorkflow: true,
    likes: 963,
    createdAt: NOW - 3 * HOUR,
    authorName: '镜头背后',
  },
  {
    id: 'demo-vid-02',
    type: 'video',
    url: '/videos/v-02.mp4',
    coverUrl: '/images/home/videos/v-02.png',
    title: '产品 360° 展示 · 黑金质感',
    categories: ['电商详情'],
    prompt: '黑色背景产品 360 度旋转展示，金色轮廓光，高级质感',
    isPublicWorkflow: false,
    likes: 421,
    createdAt: NOW - 7 * HOUR,
    authorName: '品牌视觉部',
  },
  {
    id: 'demo-vid-03',
    type: 'video',
    url: '/videos/v-03.mp4',
    coverUrl: '/images/home/videos/v-03.png',
    title: '微距水滴 · 高速摄影',
    categories: ['产品宣传KV'],
    prompt: '微距高速摄影，水滴落下溅起皇冠形水花，黑色背景',
    isPublicWorkflow: true,
    likes: 1877,
    createdAt: NOW - 12 * HOUR,
    authorName: '慢门玩家',
  },
  {
    id: 'demo-vid-04',
    type: 'video',
    url: '/videos/v-04.mp4',
    coverUrl: '/images/home/videos/v-04.png',
    title: '二次元角色登场 · 动漫风',
    categories: ['产品宣传KV'],
    prompt: '日系动漫风格角色登场镜头，速度线与光效，赛璐璐上色',
    isPublicWorkflow: true,
    likes: 654,
    createdAt: NOW - 20 * HOUR,
    authorName: '次元画师',
  },
  {
    id: 'demo-vid-05',
    type: 'video',
    url: '/videos/v-05.mp4',
    coverUrl: '/images/home/videos/v-05.png',
    title: '海边日落 · 氛围感空镜',
    categories: ['产品宣传KV'],
    prompt: '海边日落空镜头，慢镜头海浪，暖橘色天空，氛围感',
    isPublicWorkflow: false,
    likes: 298,
    createdAt: NOW - 30 * HOUR,
    authorName: '旅拍日记',
  },
  {
    id: 'demo-vid-06',
    type: 'video',
    url: '/videos/v-06.mp4',
    coverUrl: '/images/home/videos/v-06.png',
    title: '街舞卡点 · 动感剪辑',
    categories: ['产品宣传KV'],
    prompt: '街舞卡点视频，动感触控转场，霓虹灯光下的舞者',
    isPublicWorkflow: true,
    likes: 1206,
    createdAt: NOW - 45 * HOUR,
    authorName: 'Boom街舞社',
  },
];

interface WorksState {
  publishedWorks: PublishedWork[];
  libraryAssets: LibraryAsset[];
  /** 首页精选作品（服务端 /api/works/featured 返回）：会话态不持久化，
      供匿名访客点击精选卡时预览解析（此时 publishedWorks 尚未 hydrate） */
  featuredWorks: PublishedWork[];
  /** 服务端作品是否已拉取过（避免每次进入页面重复请求） */
  hydrated: boolean;
  /** 覆写精选作品会话态（WorksSection 拉取精选接口成功后写入） */
  setFeaturedWorks: (works: PublishedWork[]) => void;
  /** 发布作品：新作品前插（Bong 精选展示栏最新在前） */
  publishWork: (work: PublishedWork) => void;
  /** 调后端发布作品：成功返回作品并本地前插，失败返回 null（调用方可兜底本地发布） */
  publishToServer: (payload: WorkPublishPayload) => Promise<PublishedWork | null>;
  /** 从服务端拉取作品列表 + 存量本地作品一次性迁移（未登录则跳过，保留本地） */
  hydrateFromServer: () => Promise<void>;
  /** 历史图库归档：按 id 去重合并，新的在前 */
  addLibraryAssets: (assets: LibraryAsset[]) => void;
}

/** 防并发：hydrateFromServer 同一时间只允许一个在途请求 */
let hydrating = false;

function getAuthToken(): string | null {
  try {
    return localStorage.getItem('bongai_token');
  } catch {
    return null;
  }
}

/** 预览解析用作品列表：publishedWorks + 精选会话态去重合并。
    匿名访客的 publishedWorks 只有演示数据且不会 hydrate，首页精选卡
    （服务端作品）点击后须能在该合并列表中查到才能打开预览 */
export const selectResolvableWorks = (s: WorksState): PublishedWork[] => {
  if (!s.featuredWorks.length) return s.publishedWorks;
  const ids = new Set(s.publishedWorks.map((w) => w.id));
  return [...s.publishedWorks, ...s.featuredWorks.filter((w) => !ids.has(w.id))];
};

export const useWorksStore = create<WorksState>()(
  persist(
    (set, get) => ({
      // 初始即有演示内容：/bongworks 首次进入可直接看效果（真实发布会前插到最前）
      publishedWorks: DEMO_WORKS,
      libraryAssets: [],
      featuredWorks: [],

      hydrated: false,

      setFeaturedWorks: (works) => set({ featuredWorks: works }),

      publishWork: (work) =>
        set((state) => ({
          publishedWorks: [work, ...state.publishedWorks.filter((w) => w.id !== work.id)],
        })),

      publishToServer: async (payload) => {
        const token = getAuthToken();
        if (!token) return null; // 未登录：调用方兜底本地发布
        try {
          const res = await fetch(`${API_BASE}/api/works`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });
          if (!res.ok) return null;
          const data = await res.json();
          const work = data?.work as PublishedWork | undefined;
          if (!work?.id) return null;
          get().publishWork(work);
          return work;
        } catch {
          return null;
        }
      },

      hydrateFromServer: async () => {
        if (hydrating || get().hydrated) return;
        const token = getAuthToken();
        if (!token) return; // 未登录：保留本地（localStorage + 演示数据）
        hydrating = true;
        try {
          const res = await fetch(`${API_BASE}/api/works?limit=500`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) return; // 拉取失败：保留现状，下次再试（hydrated 仍为 false）
          const data = await res.json();
          const serverWorks: PublishedWork[] = Array.isArray(data?.works) ? data.works : [];

          // 存量迁移：localStorage 里的真实作品（非 demo- 前缀）若服务端没有，逐条补传
          //（上限 50 条防阻塞；补传失败/超出上限的保留在列表尾部，继续本地可见）
          // 按 id 或 url 双重查重——旧缓存/多标签页并发时同内容会以不同 id 存在，
          // 仅按 id 匹配会导致同一作品被反复补传成重复记录
          const serverIds = new Set(serverWorks.map((w) => w.id));
          const serverUrls = new Set(serverWorks.map((w) => w.url));
          const localOnly = get().publishedWorks.filter(
            (w) => !w.id.startsWith('demo-') && !serverIds.has(w.id) && !serverUrls.has(w.url)
          );
          const migrated: PublishedWork[] = [];
          for (const w of localOnly.slice(0, 50)) {
            const done = await get().publishToServer({
              type: w.type,
              url: w.url,
              coverUrl: w.coverUrl,
              sources: w.sources,
              images: w.images,
              coverWidth: w.coverWidth,
              coverHeight: w.coverHeight,
              title: w.title,
              categories: w.categories,
              prompt: w.prompt,
              isPublicWorkflow: w.isPublicWorkflow,
              workflowData: w.workflowData,
            });
            if (done) migrated.push(done);
          }
          const migratedIds = new Set(migrated.map((w) => w.id));
          const retryLocal = localOnly.filter((w) => !migratedIds.has(w.id));

          set({
            hydrated: true,
            // 服务端列表为真源；本地补传成功的插入最前，未同步成功的次之，演示数据垫底
            publishedWorks: [...migrated, ...serverWorks, ...retryLocal, ...DEMO_WORKS],
          });
        } catch {
          // 网络异常：保留现状，下次再试
        } finally {
          hydrating = false;
        }
      },

      addLibraryAssets: (assets) =>
        set((state) => {
          if (!assets.length) return state;
          const known = new Set(state.libraryAssets.map((a) => a.id));
          const incoming = assets.filter((a) => a && a.id && !known.has(a.id));
          if (!incoming.length) return state;
          return { libraryAssets: [...incoming, ...state.libraryAssets] };
        }),
    }),
    {
      name: 'bongai-works',
      // v2：历史持久化过的空作品列表补种演示数据（已有真实作品原样保留）
      version: 2,
      migrate: (persisted) => {
        const prev = (persisted ?? {}) as Partial<Pick<WorksState, 'publishedWorks' | 'libraryAssets'>>;
        return {
          publishedWorks: prev.publishedWorks?.length ? prev.publishedWorks : DEMO_WORKS,
          libraryAssets: prev.libraryAssets ?? [],
        };
      },
      // hydrated 是会话内状态，不持久化
      partialize: (state) => ({
        publishedWorks: state.publishedWorks,
        libraryAssets: state.libraryAssets,
      }),
    }
  )
);
